import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessTime } from '@mui/icons-material';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import { COLOR, TRANSPARENT_COLOR } from '@src/styles/color';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import { BREAK_TIME } from '@src/constants/voice';
import Typography from '@src/new-components/Typography';
import { Box, Popover, TextField } from '@mui/material';
import Tooltip from '@src/components/Tooltip';
import {
  VALID_BREAK_TIME,
  VALID_BREAK_TIME_REGEX,
} from '@src/constants/project';
import { replaceNumberWithDot } from '@src/utils/number';
import { checkValidNumber } from '@src/services/checkValid';
import {
  PopoverMenuItem,
  StyledBreakTime,
  StyledCustomBreakTime,
  StyledListMenuItem,
} from './index.style';
import CommonButton from '../Button';

const BreakTime = ({
  currentBreakTime = 1,
  isSentence,
  disabled,
  width,
  stopClickOutside = false,
  onChange,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const getTextValue = (value) => {
    if (value <= 0) return t('breakTime');

    return `${value}s`;
  };
  const [breakTime, setBreakTime] = useState();
  const [textValue, setTextValue] = useState(getTextValue(currentBreakTime));
  const [anchor, setAnchor] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [error, setError] = useState(false);
  const [openTooltip, setOpenTooltip] = useState(false);
  const inputRef = useRef(null);

  const popperContainerRef = useClickOutside(
    () => {
      setOpenDropdown(false);
      setAnchor(null);
      setBreakTime('');
      setError(false);
    },
    ['mousedown'],
    null,
    stopClickOutside,
  );

  useEffect(() => {
    setTextValue(getTextValue(breakTime));
  }, [language]);

  useEffect(() => {
    setTextValue(getTextValue(currentBreakTime));
  }, [currentBreakTime]);

  const handleDropdownClick = (event) => {
    if (disabled) return;
    setBreakTime('');
    setAnchor(event.currentTarget);
    setOpenDropdown(!openDropdown);
    setOpenTooltip(false);
    if (openDropdown && inputRef.current) {
      inputRef.current?.focus();
    }
  };

  const handleSelectedValue = (value) => {
    if (onChange) onChange(value);
    setOpenDropdown(false);
    setAnchor(null);
    setError(false);
  };

  const handleChangeBreakTime = (e) => {
    const { value } = e.target;
    const newValue = replaceNumberWithDot(value);
    setBreakTime(newValue);
  };

  const handleApplyBreakTime = () => {
    const isNumber = checkValidNumber(breakTime);
    if (!isNumber) {
      setError(true);
      return;
    }
    if (
      !VALID_BREAK_TIME_REGEX.test(breakTime) ||
      breakTime > VALID_BREAK_TIME.MAX ||
      breakTime < VALID_BREAK_TIME.MIN
    ) {
      setError(true);
      return;
    }
    setError(false);
    const breakTimeNumber = parseFloat(parseFloat(breakTime).toFixed(1));
    handleSelectedValue(breakTimeNumber);
  };

  const renderArrow = (anchorDropdown) => {
    if (anchorDropdown) {
      return (
        <ArrowDropUpRoundedIcon
          sx={{
            color: disabled ? TRANSPARENT_COLOR.lightGray : COLOR.navyBlue,
            fontSize: '24px',
          }}
        />
      );
    }
    return (
      <ArrowDropDownRoundedIcon
        sx={{
          color: disabled ? TRANSPARENT_COLOR.lightGray : COLOR.navyBlue,
          fontSize: '24px',
        }}
      />
    );
  };

  return (
    <StyledBreakTime
      isSentence={isSentence}
      disabled={disabled}
      data-break-time={textValue}
      width={width}
    >
      <Tooltip
        arrow
        title={t('addBreakTimeV2')}
        placement="top"
        open={openTooltip}
        disableHoverListener
        onMouseEnter={() => setOpenTooltip(!openDropdown)}
        onMouseLeave={() => setOpenTooltip(false)}
      >
        <div
          onClick={handleDropdownClick}
          role="button"
          tabIndex={0}
          className={`dropdown-container ${disabled ? 'disabled' : ''}`}
        >
          <AccessTime
            color={disabled ? 'disabled' : 'iconPrimary'}
            sx={{ fontSize: 24, opacity: 0.8 }}
          />
          {renderArrow(anchor)}
        </div>
      </Tooltip>
      <Popover
        open={openDropdown}
        anchorEl={anchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        className="popover-break-time"
        PaperProps={{
          style: {
            marginTop: '8px',
            borderRadius: '8px',
            width: '184px',
            paddingBottom: '8px',
          },
          sx: {
            boxShadow: '0px 4px 16px 0px #00000014 !important',
          },
        }}
      >
        <div ref={popperContainerRef}>
          <Box
            sx={{
              padding: '8px 12px',
              borderBottom: `1px solid ${COLOR.indigo['16']}`,
              boxSizing: 'border-box',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography variant="body-14-medium" style={{ color: COLOR.black }}>
              {t('breakTimeV2')}
            </Typography>
          </Box>
          <StyledListMenuItem>
            {BREAK_TIME.map((option, idx) => (
              <PopoverMenuItem
                onClick={() => {
                  if (option?.disabled) return;
                  handleSelectedValue(option);
                  setTextValue(getTextValue(option));
                }}
                key={option}
                style={{
                  borderRadius: '8px',
                  height: '40px',
                }}
                className={`dropdown__menu_item item-${idx}`}
              >
                <Typography variant="body-14-medium" color={COLOR.darkGray}>
                  {option}
                  {t('seconds')}
                </Typography>
              </PopoverMenuItem>
            ))}
          </StyledListMenuItem>
          <StyledCustomBreakTime>
            <div className="custom-break-time-wrapper">
              <TextField
                variant="outlined"
                size="small"
                className="text-field"
                placeholder={t('customize')}
                value={breakTime}
                onChange={handleChangeBreakTime}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleApplyBreakTime(breakTime);
                  }
                }}
                error={error}
                inputRef={inputRef}
                autoFocus
                sx={{
                  '& .Mui-error': {
                    borderColor: COLOR.errorDropZone,
                  },
                }}
              />
              <Box>
                <CommonButton
                  variant="containedSmall"
                  color="primary"
                  onClick={() => handleApplyBreakTime(breakTime)}
                  customStyle={{
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    padding: '4px 8px !important',
                    width: '100%',
                    borderRadius: '8px',
                    height: '32px',
                    border: 'none',
                  }}
                  disabled={!breakTime}
                >
                  {t('apply')}
                </CommonButton>
              </Box>
            </div>
            {error && (
              <Typography
                variant="body-12-regular"
                className="error-text"
                color={COLOR.functionalRed.default}
              >
                {t('validValuesBreakTime', {
                  min: VALID_BREAK_TIME.MIN,
                  max: VALID_BREAK_TIME.MAX,
                })}
              </Typography>
            )}
          </StyledCustomBreakTime>
        </div>
      </Popover>
    </StyledBreakTime>
  );
};
export default BreakTime;
